import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bus, 
  Search, 
  Clock, 
  MapPin, 
  ArrowRight, 
  Building2, 
  HelpCircle, 
  ChevronDown, 
  Compass
} from 'lucide-react';
import SEO from '../components/SEO';
import BusDisclaimer from '../components/BusDisclaimer';
import rawData from '../data/busTimeTableData.json';
import { HINDI_PLACES } from '../config/busConfig';
import { SITE_URL } from '../config/seo';

export default function BusTimeTableTohanaLocalHub() {
  const [searchTerm, setSearchTerm] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Filter originating from Tohana to destinations with < 5 buses
  const localBuses = useMemo(() => {
    const tohanaDirect = rawData.timetable.filter(
      (r) => r.bus_stand === 'Tohana' && r.from === 'Tohana'
    );
    const destCounts = {};
    tohanaDirect.forEach((r) => {
      destCounts[r.to] = (destCounts[r.to] || 0) + 1;
    });

    return tohanaDirect.filter((r) => destCounts[r.to] < 5)
      .sort((a, b) => a.to.localeCompare(b.to) || a.departure_time.localeCompare(b.departure_time));
  }, []);

  const groupedDestinations = useMemo(() => {
    const map = {};
    localBuses.forEach((b) => {
      if (!map[b.to]) map[b.to] = [];
      map[b.to].push(b);
    });
    return map;
  }, [localBuses]);

  const filteredDestinations = useMemo(() => {
    if (!searchTerm.trim()) return groupedDestinations;
    const q = searchTerm.toLowerCase();
    const result = {};
    Object.keys(groupedDestinations).forEach((dest) => {
      const buses = groupedDestinations[dest];
      const matchDest = dest.toLowerCase().includes(q);
      const matchHindi = (HINDI_PLACES[dest] || '').toLowerCase().includes(q);
      const matchBus = buses.some(
        (b) => b.via.toLowerCase().includes(q) || b.departure_time.includes(q)
      );
      if (matchDest || matchHindi || matchBus) {
        result[dest] = buses;
      }
    });
    return result;
  }, [groupedDestinations, searchTerm]);

  const pageTitle = 'Tohana Local Bus Time Table 2026 - Haryana Roadways';
  const metaDesc = 'Tohana local & rural routes bus time table 2026: 23 daily buses across 13 village routes. Check official departure schedule, bus stops & enquiry information.';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Bus Time Table',
        item: `${SITE_URL}/bus-time-table/`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Tohana Bus Stand',
        item: `${SITE_URL}/bus-time-table/tohana/`
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: 'Tohana Local Routes',
        item: `${SITE_URL}/bus-time-table/tohana-local-routes/`
      }
    ]
  };

  const faqData = [
    {
      q: 'टोहाना बस स्टैंड से लोकल रूटों पर कौन सी बसें चलती हैं?',
      a: 'टोहाना से पातरां, धनौरी, धमतान साहिब, बरवाला, संगरूर, जाखल, भूना, समैण सहित 13 स्थानीय कस्बों व ग्रामीण रूटों पर 23 दैनिक बसें चलती हैं।'
    },
    {
      q: 'टोहाना लोकल बस समय सारिणी क्या है?',
      a: 'टोहाना से लोकल रूट की बसें प्रातः 06:00 से लेकर शाम 18:30 तक नियमित अंतरालों पर प्रस्थान करती हैं।'
    }
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a
      }
    }))
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description: metaDesc,
    url: `${SITE_URL}/bus-time-table/tohana-local-routes/`,
    datePublished: '2026-10-18',
    dateModified: '2026-10-18',
    inLanguage: ['hi', 'en']
  };

  return (
    <div className="min-h-screen bg-slate-50/60 dark:bg-gray-950 text-gray-800 dark:text-gray-200">
      <SEO
        title={pageTitle}
        titleTemplate=""
        description={metaDesc}
        canonical="/bus-time-table/tohana-local-routes/"
        jsonLd={[breadcrumbJsonLd, faqJsonLd, webPageJsonLd]}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8">
        <nav aria-label="Breadcrumb" className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          <ol className="flex items-center gap-1.5 sm:gap-2">
            <li>
              <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Home
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link to="/bus-time-table" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Bus Time Table
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link to="/bus-time-table/tohana" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Tohana Stand
              </Link>
            </li>
            <li>/</li>
            <li className="font-medium text-gray-900 dark:text-gray-100">
              Tohana Local Routes
            </li>
          </ol>
        </nav>

        <div className="bg-gradient-to-br from-teal-900 via-emerald-950 to-slate-900 text-white rounded-3xl p-6 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="relative z-10 space-y-4 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 text-teal-200 text-xs font-semibold uppercase tracking-wider border border-teal-400/30">
              <Compass className="h-3.5 w-3.5" />
              Tohana Local & Rural Connectivity
            </div>
            <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
              Tohana Local & Rural Routes Bus Time Table 2026 (टोहाना लोकल बस समय सारिणी)
            </h1>
            <p className="text-sm sm:text-base text-teal-100/90 leading-relaxed">
              टोहाना बस स्टैंड से चलने वाली 13 स्थानीय कस्बों व ग्रामीण रूटों की 23 दैनिक बसों की संपूर्ण समय सारिणी। प्रस्थान समय, रूट via और ऑपरेटर विवरण देखें।
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2 text-xs sm:text-sm">
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                📍 13 Local Destinations
              </span>
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                🚌 23 Daily Departures
              </span>
            </div>
          </div>
        </div>

        <BusDisclaimer />

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5 shadow-sm">
          <div className="relative w-full">
            <Search className="absolute left-3.5 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search destination: Patran, Dhanouri, Sangrur, Barwala, Jakhal..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 text-sm sm:text-base rounded-xl border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        <div className="space-y-6">
          {Object.keys(filteredDestinations).length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-12 text-center border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400">
              कोई लोकल रूट नहीं मिला। कृपया दूसरा नाम खोजें।
            </div>
          ) : (
            Object.keys(filteredDestinations).map((dest) => {
              const buses = filteredDestinations[dest];
              const hiName = HINDI_PLACES[dest] || dest;

              return (
                <div
                  key={dest}
                  className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden"
                >
                  <div className="p-4 sm:p-5 bg-slate-50/80 dark:bg-gray-800/60 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                    <div>
                      <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-teal-600" />
                        Tohana to {dest} ({hiName})
                      </h2>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {buses.length} Daily Bus Service{buses.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="text-xs font-mono font-bold text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/60 px-2.5 py-1 rounded-md border border-teal-200 dark:border-teal-900">
                      Departs: {buses.map((b) => b.departure_time).join(', ')}
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs sm:text-sm">
                      <caption className="sr-only">Bus timings from Tohana to {dest}</caption>
                      <thead className="bg-slate-50/40 dark:bg-gray-800/30 text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800 font-semibold">
                        <tr>
                          <th scope="col" className="py-2.5 px-4 w-12 text-center">#</th>
                          <th scope="col" className="py-2.5 px-4">Departure Time</th>
                          <th scope="col" className="py-2.5 px-4">Via Route</th>
                          <th scope="col" className="py-2.5 px-4">Operator</th>
                          <th scope="col" className="py-2.5 px-4">Service Type</th>
                          <th scope="col" className="py-2.5 px-4 text-center">Days</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {buses.map((b, idx) => (
                          <tr key={`${b.serial_no}-${idx}`} className="hover:bg-teal-50/30 dark:hover:bg-teal-950/10 transition-colors">
                            <td className="py-2 px-4 text-center font-mono text-gray-400">{idx + 1}</td>
                            <td className="py-2 px-4 font-mono font-bold text-sm text-gray-900 dark:text-white">
                              <span className="inline-flex items-center gap-1 text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-950/50 px-2 py-0.5 rounded">
                                <Clock className="h-3 w-3" />
                                {b.departure_time}
                              </span>
                            </td>
                            <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{b.via}</td>
                            <td className="py-2 px-4 font-bold text-xs">{b.operator}</td>
                            <td className="py-2 px-4">
                              <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300">
                                {b.service_type}
                              </span>
                            </td>
                            <td className="py-2 px-4 text-center text-xs text-gray-500">{b.service_days || 'All Days'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* FAQs */}
        <section aria-labelledby="local-faqs-heading" className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-4">
            <HelpCircle className="h-6 w-6 text-teal-600" />
            <h2 id="local-faqs-heading" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Tohana Local Routes FAQs (अक्सर पूछे जाने वाले सवाल)
            </h2>
          </div>

          <div className="space-y-3">
            {faqData.map((f, i) => {
              const isOpen = openFaqIndex === i;
              return (
                <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between p-4 text-left font-semibold text-gray-900 dark:text-white hover:bg-slate-50 dark:hover:bg-gray-800/50 transition gap-3"
                  >
                    <span>{f.q}</span>
                    <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180 text-teal-600' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/30">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
